import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Search, Zap, Shield, Clock, CheckCircle, XCircle, Loader } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ErrorDetectivePage() {
  const { getAuthHeaders } = useAuth();
  const [errorInput, setErrorInput] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (!errorInput.trim()) {
      toast.error('Please enter error details');
      return;
    }

    setLoading(true);
    setAnalysis(null);

    try {
      const response = await axios.post(`${API_URL}/ai/error-detect`, {
        error_data: {
          message: errorInput,
          timestamp: new Date().toISOString()
        },
        deployment_context: {
          environment: 'production'
        }
      }, {
        headers: getAuthHeaders()
      });

      setAnalysis(response.data);
    } catch (error) {
      console.error('Error analysis failed:', error);
      toast.error('Failed to analyze error');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-destructive bg-destructive/20 border-destructive/30';
      case 'high': return 'text-orange-500 bg-orange-500/20 border-orange-500/30';
      case 'medium': return 'text-yellow-500 bg-yellow-500/20 border-yellow-500/30';
      default: return 'text-primary bg-primary/20 border-primary/30';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3" data-testid="error-detective-title">
            <AlertTriangle className="w-8 h-8 text-destructive" />
            Error Detective
          </h1>
          <p className="text-foreground/60 mt-1">
            AI-powered error analysis and intelligent fix suggestions
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="bg-card/50 border-border h-full">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary" />
                  Error Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={errorInput}
                  onChange={(e) => setErrorInput(e.target.value)}
                  placeholder="Paste your error message, stack trace, or describe the issue here...

Example:
Database connection timeout after 30s
Connection pool exhausted
52 failed queries in last 5 minutes"
                  className="min-h-[300px] bg-[#0A0E27] border-border font-code text-sm resize-none"
                  data-testid="error-input"
                />
                <Button
                  onClick={handleAnalyze}
                  disabled={loading || !errorInput.trim()}
                  className="w-full bg-destructive text-white font-bold hover:bg-destructive/90"
                  data-testid="analyze-error-btn"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Analyze Error
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Analysis Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="bg-card/50 border-border h-full">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-secondary" />
                  AI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!analysis && !loading && (
                  <div className="text-center py-16">
                    <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">
                      Enter error details and click analyze to get AI-powered insights
                    </p>
                  </div>
                )}

                {loading && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 border-4 border-destructive border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Analyzing error patterns...</p>
                  </div>
                )}

                {analysis && (
                  <div className="space-y-6">
                    {/* Root Cause */}
                    <div className="p-4 bg-[#0A0E27] rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-heading font-semibold text-white">Root Cause</h4>
                        <span className="text-xs text-primary">
                          {analysis.confidence || 85}% confidence
                        </span>
                      </div>
                      <p className="text-foreground/80 text-sm">
                        {analysis.rootCause || analysis.raw_response || "Analysis complete"}
                      </p>
                    </div>

                    {/* Severity */}
                    {analysis.severity && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">Severity:</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${getSeverityColor(analysis.severity)}`}>
                          {analysis.severity}
                        </span>
                      </div>
                    )}

                    {/* Recommended Fixes */}
                    {analysis.fixes && analysis.fixes.length > 0 && (
                      <div>
                        <h4 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-accent" />
                          Recommended Fixes
                        </h4>
                        <div className="space-y-3">
                          {analysis.fixes.map((fix, i) => (
                            <div key={i} className="p-3 bg-accent/5 border border-accent/20 rounded-lg">
                              <div className="flex items-start justify-between">
                                <p className="text-sm text-foreground/80">{fix.fix || fix}</p>
                                {fix.safetyLevel && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    fix.safetyLevel === 'safe' ? 'bg-accent/20 text-accent' :
                                    fix.safetyLevel === 'risky' ? 'bg-destructive/20 text-destructive' :
                                    'bg-yellow-500/20 text-yellow-500'
                                  }`}>
                                    {fix.safetyLevel}
                                  </span>
                                )}
                              </div>
                              {fix.estimatedImpact && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Impact: {fix.estimatedImpact}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Prevention */}
                    {analysis.preventionSuggestions && analysis.preventionSuggestions.length > 0 && (
                      <div>
                        <h4 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-secondary" />
                          Prevention
                        </h4>
                        <ul className="space-y-2">
                          {analysis.preventionSuggestions.map((suggestion, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-foreground/70">
                              <span className="text-secondary mt-1">•</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Rollback Recommendation */}
                    {analysis.shouldRollback !== undefined && (
                      <div className={`p-4 rounded-lg border ${
                        analysis.shouldRollback 
                          ? 'bg-destructive/10 border-destructive/30' 
                          : 'bg-accent/10 border-accent/30'
                      }`}>
                        <div className="flex items-center gap-2">
                          {analysis.shouldRollback ? (
                            <XCircle className="w-5 h-5 text-destructive" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-accent" />
                          )}
                          <span className="font-medium text-white">
                            {analysis.shouldRollback 
                              ? 'Rollback Recommended' 
                              : 'Fix In Place - No Rollback Needed'
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
